import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import DeleteModal from "../../../../components/modals/DeleteModal";
import {
    getProgressionConfig,
    getBadges,
    deleteBadge,
    updateBadgeStatus,
    BadgeResponse,
    getScanConfig,
    getScannableObjectTags,
} from "../../../../api/progression";
import i18n from "../../../../i18n";
import { compoundPluralKey } from "../../../../utils/pluralKey";
import styles from "./ManageBadges.module.css";
import Icon from "../../../../components/common/Icon";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface ManageBadgesProps {
    navigateToNext: () => void;
}

const ManageBadges: React.FC<ManageBadgesProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [badges, setBadges] = useState<BadgeResponse[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [badgeToDelete, setBadgeToDelete] = useState<BadgeResponse | null>(null);

    const linkId = trackData?.trackId || trackData?.id;

    const loadBadges = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);
            const configResponse = await getProgressionConfig({ token, trackId: trackData.trackId });
            if (configResponse?.progressionConfig) {
                setProgressionConfigId(configResponse.progressionConfig.id);
                const badgesResponse = await getBadges({
                    token,
                    progressionConfigId: configResponse.progressionConfig.id,
                });
                if (badgesResponse?.badges) {
                    setBadges(badgesResponse.badges);
                }
            }

            // Load tags for condition descriptions
            const scanConfigResponse = await getScanConfig({ token, trackId: trackData.trackId });
            if (scanConfigResponse?.scanConfig) {
                const tagsResponse = await getScannableObjectTags({
                    token,
                    scanConfigId: scanConfigResponse.scanConfig.id,
                });
                if (tagsResponse?.scannableObjectTags) {
                    setTags(tagsResponse.scannableObjectTags);
                }
            }
        } catch (error) {
            console.error("Error loading badges:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBadges();
        // fetch-on-mount when track changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, token]);

    const handleToggleStatus = async (badgeId: string, currentStatus: boolean) => {
        // Optimistic update
        setBadges((prev) => prev.map((badge) => (badge.id === badgeId ? { ...badge, status: !currentStatus } : badge)));
        try {
            await updateBadgeStatus({
                token,
                badgeId,
                status: !currentStatus,
            });
        } catch (error) {
            console.error("Error updating badge status:", error);
            // Revert on error
            setBadges((prev) =>
                prev.map((badge) => (badge.id === badgeId ? { ...badge, status: currentStatus } : badge)),
            );
        }
    };

    const handleDelete = async () => {
        if (!badgeToDelete) return;

        // Optimistic update
        const deletedBadge = badgeToDelete;
        setBadges((prev) => prev.filter((badge) => badge.id !== deletedBadge.id));
        setDeleteModalOpen(false);
        setBadgeToDelete(null);

        try {
            await deleteBadge({ token, badgeId: deletedBadge.id });
        } catch (error) {
            console.error("Error deleting badge:", error);
            // Revert on error
            setBadges((prev) => [...prev, deletedBadge]);
        }
    };

    const handleEdit = (badgeId: string) => {
        navigate(`/create/tour/${linkId}/badges/modify/${badgeId}`);
    };

    const handleCreate = () => {
        navigate(`/create/tour/${linkId}/badges/create`);
    };

    const getLocalizedName = (badge: BadgeResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = badge.badgeLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || badge.badgeLocalized?.[0]?.name || t("unnamed");
    };

    const getLocalizedText = (badge: BadgeResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = badge.badgeLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.text || badge.badgeLocalized?.[0]?.text || "";
    };

    const getLocalizedTagName = (tagId: string) => {
        const tag = tags.find((t) => t.id === tagId);
        if (!tag) return tagId;

        const langCode = i18n.language?.toUpperCase();
        const localized = tag.scannableObjectTagLocalized?.find(
            (loc: { language?: string }) => loc.language?.toUpperCase() === langCode,
        );
        return localized?.name || tag.scannableObjectTagLocalized?.[0]?.name || tagId;
    };

    const getConditionDescription = (badge: BadgeResponse) => {
        if (!badge.conditions || badge.conditions.length === 0) {
            return "";
        }

        const condition = badge.conditions[0];
        if (condition.conditionType === "TAG_COUNT") {
            const objectCount = condition.taggedObjectsRequired || 1;
            const storyCount = condition.waypointsPerObjectRequired || 1;

            if (condition.tagId) {
                const tagName = getLocalizedTagName(condition.tagId);

                if (storyCount > 1) {
                    return t(compoundPluralKey("discoversObjectsWithTagAndStories", objectCount, storyCount), {
                        count: objectCount,
                        tag: tagName,
                        storyCount,
                    });
                }
                return t("discoversObjectsWithTag", {
                    count: objectCount,
                    tag: tagName,
                });
            }

            if (storyCount > 1) {
                return t(compoundPluralKey("discoversAnyObjectsAndStories", objectCount, storyCount), {
                    count: objectCount,
                    storyCount,
                });
            }
            return t("discoversAnyObjects", {
                count: objectCount,
            });
        }
        return "";
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/levels`}>{t("progression")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("badges")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createAndManageBadges")}</h2>
                        <p className={pageHeader.subtitle}>{t("badgesDefineSpecificAccomplishments")}</p>
                    </div>
                    <div className={styles.headerActions}>
                        <AddButton onClick={handleCreate} tooltip={t("addNewBadge")} disabled={!progressionConfigId} />
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : badges.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noBadgesYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noBadgesDescription")}</p>
                    <AddButton onClick={handleCreate} tooltip={t("createFirstBadge")} disabled={!progressionConfigId} />
                </div>
            ) : (
                <div className={styles.badgesList}>
                    {badges.map((badge) => {
                        const conditionDesc = getConditionDescription(badge);
                        const badgeText = getLocalizedText(badge);
                        return (
                            <div
                                key={badge.id}
                                className={`${styles.badgeCard} ${!badge.status ? styles.badgeCardInactive : ""}`}
                                onClick={() => handleEdit(badge.id)}
                            >
                                {/* Badge image */}
                                {badge.imageUrl ? (
                                    <div className={styles.badgeImageContainer}>
                                        <img
                                            src={badge.imageUrl}
                                            alt={getLocalizedName(badge)}
                                            className={styles.badgeImage}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={styles.badgeImageContainer}
                                        style={{
                                            background: "#e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <span style={{ color: "#999", fontSize: "24px" }}>🏆</span>
                                    </div>
                                )}

                                {/* Badge info */}
                                <div className={styles.badgeInfo}>
                                    <h4 className={styles.badgeName}>{getLocalizedName(badge)}</h4>
                                    <p className={styles.badgeDescription}>
                                        {conditionDesc || badgeText || t("noDescription")}
                                    </p>
                                </div>

                                {/* Badge actions */}
                                <div className={styles.badgeActions}>
                                    <ToggleSwitch
                                        checked={badge.status}
                                        onChange={() => handleToggleStatus(badge.id, badge.status)}
                                    />
                                    <div className={styles.actionButtons}>
                                        <Tooltip title={t("edit")} arrow>
                                            <button
                                                type="button"
                                                className={styles.editBtn}
                                                aria-label={t("edit")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(badge.id);
                                                }}
                                            >
                                                <Icon name="ic-edit" size={20} />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title={t("delete")} arrow>
                                            <button
                                                type="button"
                                                className={styles.deleteBtn}
                                                aria-label={t("delete")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBadgeToDelete(badge);
                                                    setDeleteModalOpen(true);
                                                }}
                                            >
                                                <Icon name="ic-trash" size={20} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Continue — mock .foot (badges optional) */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={badges.length === 0 ? t("skipAndContinue") : t("continue")}
                    onClick={navigateToNext}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {badgeToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setBadgeToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteBadgeConfirmation")}
                    title={t("deleteBadge")}
                />
            )}
        </div>
    );
};

export default ManageBadges;
